import { getGlobalFolderViewStateForTelemetry } from "owa-analytics-utils";
import { trace } from "owa-trace";
import { flattenSessionPropertyBag } from "owa-analytics-shared";
import type { SessionScalar } from "owa-analytics-shared";
import type { SessionSettingsBeforeFolderViewBits, SessionSettingsAfterFolderViewBits, SessionSettings, } from "./SessionPropertyBags";

interface SessionSettingsSections {
    listViewType: string | undefined;
    consumerAdsExperiment: string | undefined;
    globalFolderViewState: string | null | undefined;
    beforeFolderViewBits: Readonly<SessionSettingsBeforeFolderViewBits>;
    afterFolderViewBits: Readonly<SessionSettingsAfterFolderViewBits>;
}

interface FolderViewProperties {
    isConversationView?: SessionScalar;
    folderViewBits?: Record<string, unknown> | null;
}

function getFolderViewProperties(globalFolderViewState: string | null | undefined): FolderViewProperties {
    const parsedFolderViewState = getGlobalFolderViewStateForTelemetry(globalFolderViewState);
    if (parsedFolderViewState === undefined) {
        return {};
    }
    
    if (parsedFolderViewState === null) {
        return { folderViewBits: null };
    }
    
    const { IsConversationView: isConversationView, ...folderViewState } = parsedFolderViewState;
    if (typeof isConversationView === "string" ||
        typeof isConversationView === "number" ||
        typeof isConversationView === "boolean") {
        return { isConversationView, folderViewBits: folderViewState };
    }
    if (isConversationView === null) {
        return { isConversationView: null, folderViewBits: folderViewState };
    }
    if (isConversationView !== undefined) {
        trace.warn("Unexpected GlobalFolderViewState shape in session telemetry.", "analytics");
    }
    return { folderViewBits: folderViewState };
}

const canonicalSettingsPaths = [
    ["NetworkInfo", "type"],
    ["NetworkInfo", "effectiveType"],
    ["NetworkInfo", "downlink"],
    ["NetworkInfo", "rtt"],
    ["NetworkInfo", "saveData"],
    ["Dimensions", "WindowWidth"],
    ["Dimensions", "WindowHeight"],
    ["FolderViewBits", "Width"],
    ["FolderViewBits", "IsConversationView"],
] as const;

export function serializeSessionSettings({ listViewType, consumerAdsExperiment, globalFolderViewState, beforeFolderViewBits, afterFolderViewBits, }: SessionSettingsSections): string {
    const { isConversationView, folderViewBits } = getFolderViewProperties(globalFolderViewState);
    
    const settings: SessionSettings = flattenSessionPropertyBag({
        ListViewType: listViewType,
        ConsumerAdsExperiment: consumerAdsExperiment,
        IsConversationView: isConversationView,
        ...beforeFolderViewBits,
        FolderViewBits: folderViewBits,
        ...afterFolderViewBits,
    }, { preserveNull: true, canonicalPaths: canonicalSettingsPaths });
    return JSON.stringify(settings);
}
